import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  VStack,
  SimpleGrid,
  Box,
  Heading,
  List,
  ListItem,
  ListIcon,
  Button,
  Badge,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaCheck } from 'react-icons/fa';
import { useCheckout, useBillingStatus } from '../hooks/useBilling';
import { PLAN_PRICING } from '../utils/planConfig';

interface UpgradePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string;
}

const upgradePlans = [
  {
    id: 'starter' as const,
    name: 'Starter',
    price: PLAN_PRICING.starter.price,
    description: 'Unlimited projects, clients, and file uploads.',
    features: ['Unlimited projects & clients', 'File uploads', 'Unlimited team members (per seat)'],
    highlight: false,
  },
  {
    id: 'pro' as const,
    name: 'Pro',
    price: PLAN_PRICING.pro.price,
    description: 'Analytics, role management, and advanced reporting.',
    features: ['Everything in Starter', 'Analytics dashboard', 'Role management', 'Quoted-value reporting'],
    highlight: true,
  },
];

const UpgradePlanModal: React.FC<UpgradePlanModalProps> = ({ isOpen, onClose, message }) => {
  const checkout = useCheckout();
  const { data: billing } = useBillingStatus();
  const toast = useToast();
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const highlightBg = useColorModeValue('blue.50', 'blue.900');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const handleUpgrade = (plan: 'starter' | 'pro') => {
    checkout.mutate(
      { plan, seats: billing?.activeUsers || 1 },
      {
        onError: (err: any) => {
          toast({
            title: err.response?.data?.message || 'Checkout failed',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        },
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upgrade your plan</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack align="stretch" spacing={5}>
            {message && (
              <Text fontSize="sm" color={muted}>
                {message}
              </Text>
            )}
            <Text fontSize="sm" color={muted}>
              Choose a plan to continue. You&apos;ll be redirected to secure checkout.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {upgradePlans.map((plan) => (
                <Box
                  key={plan.id}
                  p={5}
                  borderRadius="xl"
                  bg={plan.highlight ? highlightBg : cardBg}
                  borderWidth={plan.highlight ? 2 : 1}
                  borderColor={plan.highlight ? 'blue.400' : borderColor}
                  position="relative"
                >
                  {plan.highlight && (
                    <Badge
                      position="absolute"
                      top={-2}
                      right={4}
                      colorScheme="blue"
                      fontSize="xs"
                    >
                      Most popular
                    </Badge>
                  )}
                  <VStack align="start" spacing={4}>
                    <Box>
                      <Heading size="sm" mb={1}>
                        {plan.name}
                      </Heading>
                      <Text fontSize="sm" color={muted}>
                        {plan.description}
                      </Text>
                    </Box>

                    <Text fontSize="2xl" fontWeight="bold">
                      ${plan.price}
                      <Text as="span" fontSize="sm" fontWeight="normal" color={muted}>
                        {' '}
                        / user / mo
                      </Text>
                    </Text>

                    <List spacing={2} w="full">
                      {plan.features.map((feature) => (
                        <ListItem key={feature} fontSize="sm" display="flex" alignItems="flex-start">
                          <ListIcon as={FaCheck} color="green.500" mt={0.5} />
                          {feature}
                        </ListItem>
                      ))}
                    </List>

                    <Button
                      w="full"
                      colorScheme="blue"
                      size="sm"
                      isLoading={checkout.isPending}
                      onClick={() => handleUpgrade(plan.id)}
                    >
                      Upgrade to {plan.name}
                    </Button>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default UpgradePlanModal;
